import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { Highlight, Strike, TextAlign, Underline } from './editor/formatExtensions';
import {
  FiAlignCenter,
  FiAlignLeft,
  FiAlignRight,
  FiBold,
  FiCheck,
  FiChevronsLeft,
  FiChevronsRight,
  FiItalic,
  FiLink,
  FiList,
  FiMessageSquare,
  FiRotateCcw,
  FiRotateCw,
  FiType,
  FiUnderline,
  FiX,
} from 'react-icons/fi';
import Indent from './editor/indentExtension';
import Suggestions, {
  SuggestionDelete,
  SuggestionInsert,
} from './editor/suggestionExtension';
import {
  collectSuggestionRanges,
  suggestionAt,
} from './editor/suggestionHelpers';
import { captureEditorSelection } from './review/reviewThreads';

const ToolbarButton = ({
  label,
  title,
  active = false,
  disabled = false,
  onClick,
  children,
}) => (
  <button
    type="button"
    className={active ? 'is-active' : ''}
    aria-label={label}
    title={title || label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

const TextEditor = ({
  value,
  onChange,
  customStyles = {},
  placeholder = '',
  showToolbar = false,
  onComment,
  onAddComment,
  commentCount = 0,
  commentsActive = false,
  commentSaving = false,
}) => {
  const wrapperRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkInputPosition, setLinkInputPosition] = useState({ x: 0, y: 0 });
  const [selection, setSelection] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [suggestionDraft, setSuggestionDraft] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [editorTick, setEditorTick] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ strike: false }),
      Strike,
      Underline,
      Highlight,
      TextAlign,
      Indent,
      SuggestionInsert,
      SuggestionDelete,
      Suggestions,
      Placeholder.configure({
        placeholder: placeholder,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor: next }) => {
      onChange(next.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const refreshSelection = useCallback(() => {
    if (!editor || composerOpen) {
      return;
    }
    const next = captureEditorSelection(editor, wrapperRef.current);
    setSelection(next?.hasSelection ? next : null);
  }, [composerOpen, editor]);

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    const sync = () => {
      refreshSelection();
      setEditorTick((tick) => tick + 1);
    };
    editor.on('selectionUpdate', sync);
    editor.on('transaction', sync);
    editor.on('blur', sync);
    return () => {
      editor.off('selectionUpdate', sync);
      editor.off('transaction', sync);
      editor.off('blur', sync);
    };
  }, [editor, refreshSelection]);

  useEffect(() => {
    if (!editor) return undefined;

    const handleContextMenu = (e) => {
      const { from, to } = editor.state.selection;
      if (from === to) return;

      e.preventDefault();
      setLinkInputPosition({ x: e.clientX, y: e.clientY });
      setShowLinkInput(true);
      setLinkUrl('');
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('contextmenu', handleContextMenu);
    return () => {
      editorElement.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [editor]);

  const closeComposer = () => {
    setComposerOpen(false);
    setCommentDraft('');
    setSuggestionDraft('');
  };

  const openComposer = (nextSelection) => {
    const next =
      nextSelection || captureEditorSelection(editor, wrapperRef.current);
    setSelection(
      next || {
        excerpt: '',
        start_offset: null,
        end_offset: null,
        top: 56,
        left: 8,
        hasSelection: false,
      }
    );
    setComposerOpen(true);
    setCommentDraft('');
    setSuggestionDraft(next?.excerpt || '');
  };

  const handleComment = useCallback(() => {
    const next = captureEditorSelection(editor, wrapperRef.current);
    if (next?.hasSelection && onAddComment) {
      openComposer(next);
      return;
    }
    if (onComment && commentCount > 0) {
      onComment();
      return;
    }
    if (onAddComment) {
      openComposer(next);
      return;
    }
    onComment?.();
  }, [commentCount, editor, onAddComment, onComment]);

  useEffect(() => {
    if (!showToolbar || (!onComment && !onAddComment)) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || !event.altKey) {
        return;
      }
      if (String(event.key || '').toLowerCase() !== 'm') {
        return;
      }
      const root = wrapperRef.current;
      if (root && !root.contains(event.target)) {
        return;
      }
      event.preventDefault();
      handleComment();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleComment, onAddComment, onComment, showToolbar]);

  const submitComment = async () => {
    const replacement = suggesting ? suggestionDraft.trim() : '';
    if (!onAddComment || (!commentDraft.trim() && !replacement)) {
      return;
    }
    await onAddComment({
      excerpt: selection?.excerpt || '',
      comment: commentDraft.trim() || (replacement ? 'Suggested edit.' : ''),
      start_offset: selection?.start_offset ?? null,
      end_offset: selection?.end_offset ?? null,
      suggestion_text: replacement || null,
    });
    closeComposer();
    setSelection(null);
    if (typeof window !== 'undefined') {
      window.getSelection()?.removeAllRanges();
    }
  };

  const suggestionCount = editor
    ? collectSuggestionRanges(editor.state.doc).length
    : 0;
  const activeSuggestion = editor
    ? suggestionAt(editor.state.doc, editor.state.selection.from)
    : null;
  void editorTick;

  const setMode = (enabled) => {
    if (!editor) {
      return;
    }
    editor.commands.setSuggesting(enabled);
    setSuggesting(enabled);
    editor.commands.focus();
  };

  const handleAddLink = () => {
    if (!linkUrl.trim() || !editor) return;

    let formattedUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    editor.chain().focus().setLink({ href: formattedUrl }).run();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const handleCancelLink = () => {
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const openLinkPrompt = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href || '';
    setLinkUrl(previous);
    setLinkInputPosition({
      x: Math.min(window.innerWidth - 40, 220),
      y: 120,
    });
    setShowLinkInput(true);
  };

  return (
    <div
      ref={wrapperRef}
      className={`editor-wrapper${showToolbar ? ' has-toolbar' : ''}`}
      style={{ ...styles.editorWrapper, ...customStyles.wrapper }}
    >
      {showToolbar && editor ? (
        <div className="editor-toolbar" role="toolbar" aria-label="Text formatting">
          <ToolbarButton
            label="Undo"
            title="Undo"
            disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <FiRotateCcw size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Redo"
            title="Redo"
            disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <FiRotateCw size={16} />
          </ToolbarButton>
          <span className="editor-toolbar__divider" aria-hidden="true" />
          <ToolbarButton
            label="Bold"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FiBold size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FiItalic size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FiUnderline size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Highlight"
            title="Highlight"
            active={editor.isActive('highlight')}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          >
            <span className="editor-toolbar__highlight" aria-hidden="true">
              A
            </span>
          </ToolbarButton>
          <ToolbarButton
            label="Clear formatting"
            title="Clear formatting"
            onClick={() => editor.chain().focus().clearFormatting().run()}
          >
            <FiX size={16} />
          </ToolbarButton>
          <span className="editor-toolbar__divider" aria-hidden="true" />
          <ToolbarButton
            label="Heading"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <FiType size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Align left"
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FiAlignLeft size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Align center"
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FiAlignCenter size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Align right"
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FiAlignRight size={16} />
          </ToolbarButton>
          <span className="editor-toolbar__divider" aria-hidden="true" />
          <ToolbarButton
            label="Bullets"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FiList size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Numbers"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <span className="editor-toolbar__ordered" aria-hidden="true">
              1.
            </span>
          </ToolbarButton>
          <ToolbarButton
            label="Decrease indent"
            title="Decrease indent"
            onClick={() => editor.chain().focus().outdent().run()}
          >
            <FiChevronsLeft size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Increase indent"
            title="Increase indent"
            onClick={() => editor.chain().focus().indent().run()}
          >
            <FiChevronsRight size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Link"
            active={editor.isActive('link')}
            onClick={openLinkPrompt}
          >
            <FiLink size={16} />
          </ToolbarButton>
          {onComment || onAddComment ? (
            <>
              <span className="editor-toolbar__divider" aria-hidden="true" />
              <button
                type="button"
                className={commentsActive || composerOpen ? 'is-active' : ''}
                aria-label={
                  commentCount ? `Comments (${commentCount})` : 'Comments'
                }
                title="Comments (Ctrl/⌘+Alt+M)"
                aria-pressed={commentsActive || composerOpen}
                onClick={handleComment}
              >
                <FiMessageSquare size={16} />
                {commentCount ? (
                  <span className="editor-toolbar__badge">{commentCount}</span>
                ) : null}
              </button>
            </>
          ) : null}
          <span className="editor-toolbar__divider" aria-hidden="true" />
          <div className="editor-mode" role="group" aria-label="Editing mode">
            <button
              type="button"
              className={!suggesting ? 'is-active' : ''}
              aria-pressed={!suggesting}
              onClick={() => setMode(false)}
            >
              Editing
            </button>
            <button
              type="button"
              className={`editor-mode__suggest${suggesting ? ' is-active' : ''}`}
              aria-pressed={suggesting}
              onClick={() => setMode(true)}
            >
              Suggesting
            </button>
          </div>
          {suggestionCount ? (
            <>
              <button
                type="button"
                className="editor-toolbar__wide"
                aria-label={`Accept all suggestions (${suggestionCount})`}
                title="Accept all suggestions"
                onClick={() => editor.chain().focus().acceptAllSuggestions().run()}
              >
                <FiCheck size={14} /> Accept all
              </button>
              <button
                type="button"
                className="editor-toolbar__wide"
                aria-label="Reject all suggestions"
                title="Reject all suggestions"
                onClick={() => editor.chain().focus().rejectAllSuggestions().run()}
              >
                <FiX size={14} /> Reject all
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      <EditorContent editor={editor} className="tiptap-editor" />

      {showToolbar && activeSuggestion && !composerOpen ? (
        <div
          className="editor-suggestion-card"
          style={{
            top: Math.max(48, selection?.top || 56),
            left: Math.max(8, selection?.left || 8),
          }}
        >
          <p>
            {activeSuggestion.type === 'suggestionInsert'
              ? 'Suggested addition'
              : 'Suggested deletion'}
          </p>
          <div className="editor-suggestion-card__actions">
            <button
              type="button"
              onClick={() => editor.chain().focus().rejectSuggestion().run()}
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().acceptSuggestion().run()}
            >
              Accept
            </button>
          </div>
        </div>
      ) : null}

      {showToolbar && onAddComment && selection?.hasSelection && !composerOpen && !activeSuggestion ? (
        <button
          type="button"
          className="editor-comment-fab"
          style={{ top: selection.top, left: selection.left }}
          aria-label="Add comment"
          title="Add comment"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => openComposer(selection)}
        >
          <FiMessageSquare size={16} />
        </button>
      ) : null}

      {showToolbar && onAddComment && composerOpen ? (
        <div
          className="editor-comment-popover"
          style={{
            top: Math.max(48, selection?.top || 56),
            left: Math.max(8, selection?.left || 8),
          }}
        >
          <p className="editor-comment-popover__excerpt">
            {selection?.excerpt ? `“${selection.excerpt}”` : 'Whole document'}
          </p>
          {suggesting ? (
            <label className="editor-comment-popover__suggest">
              Replace with
              <textarea
                value={suggestionDraft}
                onChange={(event) => setSuggestionDraft(event.target.value)}
                placeholder="Suggested text"
              />
            </label>
          ) : null}
          <textarea
            value={commentDraft}
            onChange={(event) => setCommentDraft(event.target.value)}
            placeholder={suggesting ? 'Why this change? Use @ to mention.' : 'Write a comment. Use @ to mention.'}
            autoFocus
          />
          <div className="editor-comment-popover__actions">
            <button type="button" onClick={closeComposer}>
              Cancel
            </button>
            <button
              type="button"
              disabled={commentSaving || (!commentDraft.trim() && !(suggesting && suggestionDraft.trim()))}
              onClick={submitComment}
            >
              {suggesting ? 'Suggest' : 'Comment'}
            </button>
          </div>
        </div>
      ) : null}

      {showLinkInput && (
        <>
          <div className="link-input-overlay" onClick={handleCancelLink} />
          <div
            className="link-input-modal"
            style={{
              position: 'fixed',
              left: `${linkInputPosition.x}px`,
              top: `${linkInputPosition.y}px`,
              zIndex: 10000,
            }}
          >
            <div className="link-input-header">Add Link</div>
            <input
              type="text"
              className="link-input-field"
              placeholder="Enter URL (e.g., https://example.com)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddLink();
                } else if (e.key === 'Escape') {
                  handleCancelLink();
                }
              }}
              autoFocus
            />
            <div className="link-input-actions">
              <button className="link-input-btn cancel" onClick={handleCancelLink}>
                Cancel
              </button>
              <button
                className="link-input-btn apply"
                onClick={handleAddLink}
                disabled={!linkUrl.trim()}
              >
                Add Link
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  editorWrapper: {
    position: 'relative',
    width: '100%',
    margin: 'auto',
    border: '1px solid #ddd',
    borderRadius: '8px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: '150px',
    boxSizing: 'border-box',
  },
};

const editorStyles = `
  .tiptap {
    width: 100%;
    height: 400px !important;
    color: var(--community-text-secondary);
    flex: 1 1 auto;
    max-height: 400px;
    min-height: 20px;
    box-sizing: border-box;
    overflow-y: auto;
    padding: 10px;
  }
  .editor-wrapper:focus-within {
    border: none !important;
  }
  .editor-wrapper.has-toolbar:focus-within {
    border: 1px solid #ddd !important;
  }
  .editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px;
    border-bottom: 1px solid #e8e4ef;
    background: #faf8fc;
  }
  .editor-toolbar button {
    position: relative;
    width: 36px;
    min-width: 36px;
    min-height: 36px;
    padding: 0;
    border: 1px solid #d8d2e0;
    border-radius: 8px;
    background: #fff;
    color: #1d1b20;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .editor-toolbar button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .editor-toolbar__divider {
    width: 1px;
    align-self: stretch;
    margin: 2px 4px;
    background: #d8d2e0;
  }
  .editor-toolbar__badge {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: #c5221f;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
  }
  .editor-toolbar__ordered {
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
  }
  .editor-toolbar__highlight {
    font-size: 13px;
    font-weight: 800;
    line-height: 1;
    padding: 1px 3px;
    border-radius: 3px;
    background: #ffe08a;
  }
  .editor-toolbar button.is-active,
  .editor-toolbar button:hover:not(:disabled) {
    background: #262730;
    color: #fff;
    border-color: #262730;
  }
  .editor-toolbar button.is-active .editor-toolbar__highlight,
  .editor-toolbar button:hover:not(:disabled) .editor-toolbar__highlight {
    background: #ffd24d;
    color: #1d1b20;
  }
  .editor-toolbar__wide {
    width: auto !important;
    min-width: 0;
    padding: 0 10px !important;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
  }
  .editor-mode {
    display: inline-flex;
    border: 1px solid #d8d2e0;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }
  .editor-mode button {
    width: auto;
    min-width: 0;
    border: 0;
    border-radius: 0;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 700;
  }
  .editor-mode button.is-active,
  .editor-mode button.editor-mode__suggest.is-active {
    background: #262730;
    color: #fff;
  }
  .editor-mode button.editor-mode__suggest.is-active {
    background: #0d652d;
    border-color: #0d652d;
  }
  .tiptap ins.suggestion-insert,
  .tiptap .suggestion-insert {
    color: #0d652d;
    background: #e6f4ea;
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
    border-radius: 2px;
  }
  .tiptap del.suggestion-delete,
  .tiptap .suggestion-delete {
    color: #a50e0e;
    background: #fce8e6;
    text-decoration: line-through;
    border-radius: 2px;
  }
  .editor-suggestion-card {
    position: absolute;
    z-index: 9;
    width: min(260px, calc(100% - 16px));
    padding: 10px 12px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e0e0e0;
    box-shadow: 0 10px 28px rgba(29, 27, 32, 0.16);
    display: grid;
    gap: 8px;
  }
  .editor-suggestion-card p {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
  }
  .editor-suggestion-card__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .editor-suggestion-card__actions button {
    min-height: 36px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: #fff;
    font-weight: 600;
    cursor: pointer;
  }
  .editor-suggestion-card__actions button:last-child {
    background: #0d652d;
    color: #fff;
    border-color: #0d652d;
  }
  .tiptap mark,
  .tiptap .editor-highlight {
    background: #ffe08a;
    border-radius: 2px;
    padding: 0 1px;
  }
  .tiptap p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: var(--community-text-secondary);
    font-weight: 700;
    opacity: 0.5;
    pointer-events: none;
    height: 0;
  }
  .editor-comment-fab,
  .editor-comment-popover {
    position: absolute;
    z-index: 8;
  }
  .editor-comment-fab {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 1px solid #d8d2e0;
    background: #fff;
    color: #1d1b20;
    box-shadow: 0 8px 20px rgba(29, 27, 32, 0.16);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .editor-comment-popover {
    width: min(340px, calc(100% - 16px));
    padding: 12px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e0e0e0;
    box-shadow: 0 10px 28px rgba(29, 27, 32, 0.16);
    display: grid;
    gap: 8px;
  }
  .editor-comment-popover__excerpt {
    margin: 0;
    padding-left: 10px;
    border-left: 3px solid #262730;
    color: #4a4458;
    font-style: italic;
  }
  .editor-comment-popover__suggest {
    display: grid;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #6b6570;
  }
  .editor-comment-popover textarea {
    width: 100%;
    min-height: 72px;
    box-sizing: border-box;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 10px;
  }
  .editor-comment-popover__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .editor-comment-popover__actions button {
    min-height: 36px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: #fff;
    font-weight: 600;
    cursor: pointer;
  }
  .editor-comment-popover__actions button:last-child {
    background: #262730;
    color: #fff;
    border-color: #262730;
  }
  .editor-comment-popover__actions button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .editor-link {
    color: var(--primary, rgb(38, 39, 48));
    text-decoration: none;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: var(--muted, rgba(220, 218, 245, 0.3));
    border-bottom: 1.5px solid var(--primary, rgb(38, 39, 48));
    transition: all 0.2s ease;
    cursor: pointer;
  }
  .editor-link:hover {
    background-color: var(--accent, rgba(234, 221, 249, 0.6));
    transform: translateY(-1px);
  }
  .link-input-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(29, 27, 32, 0.5);
    backdrop-filter: blur(4px);
    z-index: 9999;
  }
  .link-input-modal {
    background: var(--card, rgb(248, 241, 246));
    border-radius: var(--radius-lg, 0.75rem);
    box-shadow: var(--shadow-xl, 0 4px 8px -2px rgba(0, 0, 0, 0.1));
    border: 1px solid var(--border, rgb(224, 224, 224));
    padding: 20px;
    min-width: 380px;
    transform: translate(-50%, 10px);
  }
  .link-input-header {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--foreground, rgb(29, 27, 32));
    font-family: var(--font-sans, Inter);
  }
  .link-input-field {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid var(--border, rgb(224, 224, 224));
    border-radius: var(--radius-md, 0.5rem);
    font-size: 14px;
    margin-bottom: 16px;
    box-sizing: border-box;
    transition: all 0.2s;
    background: var(--background, #fefbff);
    color: var(--foreground, rgb(29, 27, 32));
    font-family: var(--font-sans, Inter);
  }
  .link-input-field:focus {
    outline: none;
    border-color: var(--primary, rgb(38, 39, 48));
    box-shadow: 0 0 0 3px var(--muted, rgba(220, 218, 245, 0.4));
  }
  .link-input-field::placeholder {
    color: var(--muted-foreground, rgb(74, 68, 88));
    opacity: 0.7;
  }
  .link-input-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }
  .link-input-btn {
    padding: 10px 20px;
    border-radius: var(--radius-lg, 0.75rem);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-family: var(--font-sans, Inter);
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .link-input-btn.cancel {
    background: var(--muted, rgb(220, 218, 245));
    color: var(--muted-foreground, rgb(74, 68, 88));
    border: 1.5px solid var(--border, rgb(224, 224, 224));
  }
  .link-input-btn.cancel:hover {
    background: var(--accent, rgb(234, 221, 249));
    transform: translateY(-1px);
  }
  .link-input-btn.apply {
    background: var(--primary, rgb(38, 39, 48));
    color: var(--primary-foreground, rgb(255, 255, 255));
  }
  .link-input-btn.apply:hover:not(:disabled) {
    background: var(--secondary, rgb(98, 91, 113));
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg, 0 4px 8px -2px rgba(0, 0, 0, 0.1));
  }
  .link-input-btn.apply:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (max-width: 800px) {
    .editor-comment-popover,
    .editor-comment-fab,
    .editor-suggestion-card {
      position: fixed;
      left: 12px !important;
      right: 12px;
      width: auto;
      top: auto !important;
      bottom: 96px;
    }
    .editor-comment-fab {
      left: auto !important;
      right: 16px;
      width: 44px;
      height: 44px;
    }
  }
`;

const StyleInjector = ({ customStyles = {} }) => {
  useEffect(() => {
    const styleTag = document.createElement('style');
    const mergedStyles = editorStyles + (customStyles.editorContent || '');
    styleTag.innerHTML = mergedStyles;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, [customStyles]);
  return null;
};

const Editor = (props) => {
  return (
    <>
      <StyleInjector customStyles={props.customStyles} />
      <TextEditor {...props} />
    </>
  );
};

export default Editor;
